import React from "react";
import { useParams, useSearchParams } from "react-router-dom";
import type { Row as TanRow } from "@tanstack/react-table";
import type { ReportMetricRow } from "@core/components/ReportTreeTable";
import { parseReportViewSearch } from "@core/reports/constants/reportSpaQueryParams";
import { useReportPage } from "@core/reports/hooks/useReportPage";
import ReportPageTemplate, {
  type TemplatePreviewContext,
} from "@core/reports/components/ReportPageTemplate";
import RequestReviewModal from "@core/reports/components/RequestReviewModal";
import TemplateRowPreviewButton from "@core/reports/components/TemplateRowPreviewButton";
import TemplateRowReviewAction from "@core/reports/components/TemplateRowReviewAction";
import { getWorkflowStatusLabel } from "@core/reports/utils/reportWorkflow";
import { requestActivityEntryReviewAsync } from "@features/report/activity-entry/api/activityEntryApi";

export default function ChurchReportPage() {
  const { churchId } = useParams<{ churchId: string }>();
  const [searchParams] = useSearchParams();
  const numericChurchId = Number(churchId) || 0;
  const { departmentId, reportTagId } = parseReportViewSearch(searchParams);

  const report = useReportPage({
    scope: "church",
    entityId: numericChurchId,
    departmentId,
    reportTagId,
  });
  const { canLoad, statusName, isMissingReport, notesEditable, load, loadDepartmentReport, permissionsLoading, canReviewActivity } = report;

  const [showReviewModal, setShowReviewModal] = React.useState(false);
  const [selectedTemplateName, setSelectedTemplateName] = React.useState<string | null>(null);
  const [selectedReportTemplateId, setSelectedReportTemplateId] = React.useState<number | null>(null);
  const [requestingReview, setRequestingReview] = React.useState(false);
  const [reviewError, setReviewError] = React.useState<string | null>(null);

  const handleConfirmReview = async ({ reason }: { reason: string }) => {
    if (!canLoad || !selectedReportTemplateId || selectedReportTemplateId <= 0) {
      setReviewError("Report template is required.");
      return;
    }
    setRequestingReview(true);
    setReviewError(null);
    try {
      const result = await requestActivityEntryReviewAsync(
        numericChurchId,
        selectedReportTemplateId,
        reportTagId,
        { review_notes: reason }
      );
      if (!result.isSuccess()) throw new Error(result.getError()?.detail || "Failed to request review.");
      setShowReviewModal(false);
      await load();
      await loadDepartmentReport();
    } catch (e) {
      setReviewError(e instanceof Error ? e.message : "Failed to request review.");
    } finally {
      setRequestingReview(false);
    }
  };

  const renderRowName = React.useCallback(
    (row: TanRow<ReportMetricRow>, ctx: TemplatePreviewContext) => {
      const original = row.original ?? {};
      const name = String(original.name ?? "");
      const rowMeta = original.__meta as { template_id?: number; is_sub_group?: boolean } | undefined;
      const reportTemplateId =
        Number(
          rowMeta?.template_id ??
            (original as { template_id?: number; report_template_id?: number }).template_id ??
            (original as { report_template_id?: number }).report_template_id
        ) || 0;
      const isTemplateRow = row.depth === 0;
      const isSubGroup =
        original.__rowType === "sub_group" ||
        rowMeta?.is_sub_group === true ||
        (!isTemplateRow && reportTemplateId > 0);
      const showEye = isTemplateRow || isSubGroup;
      const activityStatus =
        (original.activity_report_status_name as string | null | undefined) ??
        (original.activity_entry_status_name as string | null | undefined) ??
        null;
      return (
        <div
          style={{
            display: "flex",
            alignItems: "flex-start",
            gap: 10,
            width: "100%",
            minWidth: 0,
          }}
        >
          {showEye ? (
            <TemplateRowPreviewButton
              onPreview={() => ctx.onPreview({ name: name || "Template", row: original })}
            />
          ) : null}
          {isTemplateRow ? (
            <TemplateRowReviewAction
              activityStatus={activityStatus}
              parentDepartmentStatus={statusName}
              canReviewActivity={canReviewActivity}
              permissionsLoading={permissionsLoading}
              busy={requestingReview}
              disabled={reportTemplateId <= 0}
              onRequestReview={() => {
                setSelectedTemplateName(name);
                setSelectedReportTemplateId(reportTemplateId);
                setReviewError(null);
                setShowReviewModal(true);
              }}
            />
          ) : null}
          <span
            style={{
              flex: "1 1 auto",
              minWidth: 0,
              fontWeight: isTemplateRow ? 600 : 500,
              whiteSpace: "normal",
              overflowWrap: "normal",
              lineHeight: 1.25,
            }}
            title={name}
          >
            {name}
          </span>
        </div>
      );
    },
    [canReviewActivity, permissionsLoading, requestingReview, statusName]
  );

  // Church-specific copy (preserved verbatim from the original page).
  const statusNote = notesEditable
    ? null
    : statusName === "SUBMITTED"
      ? `${getWorkflowStatusLabel("SUBMITTED")} department reports are view-only until validation or review moves them to ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`
      : statusName === "AWAITING_APPROVAL"
        ? `${getWorkflowStatusLabel("AWAITING_APPROVAL")} reports are locked while approval is pending.`
        : statusName === "APPROVED"
          ? `${getWorkflowStatusLabel("APPROVED")} reports are locked and view-only.`
          : "This report is view-only in its current workflow state.";

  const notesHint =
    isMissingReport || statusName === "NOT_STARTED"
      ? "No report started for this period. Add notes, then Save Draft to create it."
      : notesEditable
        ? `Notes are editable while the report is in ${getWorkflowStatusLabel("DRAFT")} or ${getWorkflowStatusLabel("NEEDS_ATTENTION")}.`
        : "Notes are read-only once the report has been submitted for review or approval.";

  return (
    <ReportPageTemplate
      report={report}
      scopeNoun="church"
      renderRowName={renderRowName}
      statusNote={statusNote}
      notesHint={notesHint}
      notesEditablePlaceholder="Enter department-level notes for this reporting period..."
      emptyStatePrompt="Open this page from the church dashboard or departments list (e.g. View Reports) so the URL includes the department and reporting period."
    >
      <RequestReviewModal
        show={showReviewModal}
        onHide={() => {
          if (!requestingReview) setShowReviewModal(false);
        }}
        subject={
          <>
            You are requesting review for:{" "}
            <span className="fw-semibold">{selectedTemplateName ?? "Template"}</span>
          </>
        }
        busy={requestingReview}
        error={reviewError}
        onConfirm={handleConfirmReview}
      />
    </ReportPageTemplate>
  );
}
